import {
  SanityProductFragment,
  SanityProductVariantFragment,
} from '@data/sanity/queries/types/product'
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { LanguageContext } from './language-context'
import { MetadataContext } from './metadata-context'
import { useActiveVariant, useProductWithInventory } from './product-hooks'
import { getDefaultVariantId } from './product-variant'

interface ProductContextProps {
  product?: SanityProductFragment
  activeVariant?: SanityProductVariantFragment
  setActiveVariantId: (variantId: number) => void
}

interface StringsContextProviderProps {
  product?: SanityProductFragment
  children: ReactNode
}

export const ProductContext = createContext<ProductContextProps>({
  setActiveVariantId: () => null,
})

export const ProductContextProvider = ({
  product: originalProduct,
  children,
}: StringsContextProviderProps) => {
  const { locale } = useContext(LanguageContext)
  const { setMetadataOverride } = useContext(MetadataContext)

  const [product, setProduct] = useState<SanityProductFragment>()

  // Active product
  const activeProduct = useMemo(() => {
    return product ?? originalProduct
  }, [originalProduct, product])
  useEffect(() => {
    setProduct(originalProduct)
  }, [originalProduct])

  const activeProductWithInventory = useProductWithInventory(
    locale,
    activeProduct,
  )

  // Active variant
  const defaultVariantId = useMemo(() => {
    return getDefaultVariantId(
      activeProductWithInventory?.variants,
      activeProductWithInventory?.options,
      activeProductWithInventory?.optionSettings,
    )
  }, [activeProductWithInventory])

  const [activeVariant, setActiveVariantId] = useActiveVariant(
    defaultVariantId,
    activeProductWithInventory?.variants,
  )

  // Metadata override
  useEffect(() => {
    setMetadataOverride(activeVariant?.seo ?? activeProduct?.seo ?? null)

    return () => {
      setMetadataOverride(null)
    }
  }, [activeProduct?.seo, activeVariant?.seo, setMetadataOverride])

  return (
    <ProductContext.Provider
      value={{
        product: activeProductWithInventory,
        activeVariant,
        setActiveVariantId,
      }}
    >
      {children}
    </ProductContext.Provider>
  )
}
